import polyline from '@mapbox/polyline'
import { polygon, convertLength, point, type Units } from '@turf/turf'
import type { Position } from 'geojson'
import turfRhumbDistance from '@turf/rhumb-distance'
import dateFormat from 'dateformat'
import { offlineServiceState } from '@/services/offline-map-service/'
import { DEFAULT_NAME_REGEX } from '@/services/offline-map-service/constants'
import type {
  DistancesFromPoints,
  DraftMap,
  OfflineMapsV2Payload,
  OfflineRegion,
  TransformedRegionToGeoJson,
  UnitType,
} from '@/services/offline-map-service/types'
/**
 * Transforms offline map service api response objects
 *
 * Adds GeoJson geometry property by converting polyline to a GeoJson Polygon
 *
 * Adds additional properties fields
 */
const transformRegionToGeoJson = (
  map: OfflineRegion
): TransformedRegionToGeoJson => {
  const geoJSON = polyline.toGeoJSON(map.polyline)

  return {
    ...map,
    ...polygon([geoJSON.coordinates]),
    properties: {
      id: map.uuid,
      selectable: true,
    },
  }
}

/**
 * Transforms offline map or array of maps
 *
 * For each map object we transform the coordinates to a GeoJson polygon
 * and add an additional properties field with values
 *
 * If input is OfflineRegion expected output is TransformedRegionToGeoJson
 * If input is OfflineServiceResponse expected output is TransformedRegionToGeoJson[]
 */
export const transformOfflineRegionsOrRegionToGeoJson = <TParam, TReturn>(
  mapsOrMap: TParam | TParam[]
): TReturn | TReturn[] => {
  if (Array.isArray(mapsOrMap)) {
    // If input is an array, apply the modification function to each object in the array
    return mapsOrMap.map((map) =>
      transformRegionToGeoJson(map as TransformedRegionToGeoJson)
    ) as TReturn[]
  }
  // If input is a single object, apply the modification function directly
  return transformRegionToGeoJson(
    mapsOrMap as TransformedRegionToGeoJson
  ) as TReturn
}

/**
 * Type Guard for TransformedRegionToGeoJson
 *
 * Checks for the presence of the `geometry` property in the map object
 *
 * We could check against other properties to confirm the type but `TransformedRegionToGeoJson` will always
 * have a `geometry` property since we add it to the api region response when we run the `transformRegionToGeoJson` method.
 *
 * Returns true if mapOrRegion is a TransformedRegionToGeoJson, false if mapOrRegion is a DraftMap
 */
const isTransformedRegionToGeoJson = (
  mapOrRegion: DraftMap | TransformedRegionToGeoJson
): mapOrRegion is TransformedRegionToGeoJson => {
  return (
    'geometry' in mapOrRegion &&
    (typeof mapOrRegion.geometry === 'object' ||
      mapOrRegion.geometry !== undefined)
  )
}

/**
 * Transforms a **DraftMap** or **TransformedRegionToGeoJson** object to **OfflineMapsV2Payload**
 *
 * Used in createOfflineMap or updateOfflineMap
 *
 * If called from createOfflineMap object passed in matches DraftMap type
 * If called from updateOfflineMap object passed in matches TransformedRegionToGeoJson type
 *
 */
export const transformOfflineMapsPayload = (
  draftMapOrMapRegion: DraftMap | TransformedRegionToGeoJson
): OfflineMapsV2Payload => {
  const now = new Date()
  const isoNow = dateFormat(now, 'isoUtcDateTime')
  const isDraft = !isTransformedRegionToGeoJson(draftMapOrMapRegion)
  let latLonCoords: [number, number][] = []

  if (isDraft && draftMapOrMapRegion.shape) {
    latLonCoords = draftMapOrMapRegion.shape.map(([long, lat]) => [lat, long])
  }

  const draftMapOrMapRegionPolyline: string = isDraft
    ? polyline.encode(latLonCoords)
    : draftMapOrMapRegion.polyline

  return {
    app: draftMapOrMapRegion.app ?? '',
    created_at: isDraft ? isoNow : draftMapOrMapRegion.created_at,
    map_detail: isDraft
      ? (draftMapOrMapRegion.mapDetail ?? '')
      : draftMapOrMapRegion.map_detail,
    name: isDraft
      ? (draftMapOrMapRegion.name ??
        `Map ${dateFormat(isoNow, 'mm/dd/yy HH:MM')}`)
      : (draftMapOrMapRegion.name ??
        `Map ${dateFormat(draftMapOrMapRegion.created_at, 'mm/dd/yy HH:MM')}`),
    notes: draftMapOrMapRegion.notes || '',
    polyline: draftMapOrMapRegionPolyline,
    region_version: isDraft ? 0 : draftMapOrMapRegion.region_version,
    updated_at: isoNow,
    uuid: draftMapOrMapRegion.uuid ?? '',
  }
}

/**
 * Formats a byte count as a user-visible offline map size string: whole MB up to
 * (but not including) 1024 MB, then GB with one decimal place.
 *
 * Matches the cross-platform convention (iOS/Android) where 1 MB = 1024 * 1024
 * bytes and 1 GB = 1024 MB, so the size shown here is consistent across the
 * offline map creation flow and the mobile offline maps sheet. See FOX-15117.
 */
export const formatOfflineMapSize = (bytes: number): string => {
  const sizeInMB = Math.floor(bytes / (1024 * 1024))
  if (sizeInMB < 1024) {
    return `${sizeInMB} MB`
  }
  return `${(sizeInMB / 1024).toFixed(1)} GB`
}

/**
 * Regex test map name to determine if the name is a custom name
 */
export const isCustomName = (name: string): boolean =>
  !DEFAULT_NAME_REGEX.test(name)

/**
 * Update offlineServiceState.map_area
 *
 * Set when a user is creating a map, used in marketing event offline_map_definition_saved
 *
 * Set when a user deletes the map, used in marketing event offline_map_deleted
 */
export const updateOfflineStateMapArea = (
  widthDistance: number,
  heightDistance: number,
  unitType: 'imperial' | 'metric'
): void => {
  if (!widthDistance || !heightDistance || !unitType) {
    return undefined
  }

  const map_area =
    unitType === 'imperial'
      ? (
          convertLength(widthDistance, 'miles', 'kilometers') *
          convertLength(heightDistance, 'miles', 'kilometers')
        ).toFixed(4)
      : (widthDistance * heightDistance).toFixed(4)
  offlineServiceState.map_area = Number(map_area)
}

/**
 * Get width and height from three points of a rectangular GeoJson Polygon
 *
 * **widthDistance** is the difference between pointOne and pointTwo
 *
 * **heightDistance** is the difference between pointTwo and pointThree
 */
export const getDistancesFromPoints = (
  pointOne: Position,
  pointTwo: Position,
  pointThree: Position,
  unitType: UnitType
): DistancesFromPoints => {
  const widthCoordOne = point(pointOne)
  const widthCoordTwo = point(pointTwo)

  const heightCoordOne = point(pointTwo)
  const heightCoordTwo = point(pointThree)
  const unitMeasurement = unitType === 'imperial' ? 'miles' : 'kilometers'
  const units: { units: Units } = { units: unitMeasurement }
  const widthDistance = turfRhumbDistance(widthCoordOne, widthCoordTwo, units)
  const heightDistance = turfRhumbDistance(
    heightCoordOne,
    heightCoordTwo,
    units
  )
  return { widthDistance, heightDistance }
}
